/**
 * Workflow Service
 *
 * Purpose: Handles all API communications for workflow management including
 *          workflow definitions, workflow steps, and related operations
 *
 * Architecture: Service layer that interfaces with the workflow backend
 *              API endpoints, providing typed methods for all CRUD operations
 *
 * Dependencies: ApiService
 * Author: Development Team
 * Created: 2026-04-14
 */

import { apiService } from './api';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface ScopeInfo {
  scope: 'GLOBAL' | 'Overridden' | 'LOCAL';
  is_override: boolean;
  has_global_parent: boolean;
}

export interface UserInfo {
  email: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
}

export interface WorkflowDefinition {
  workflow_id: string;
  tenant_id: string;
  label: string;
  description?: string | null;
  models: string[];
  status: 'active' | 'disabled' | 'deleted';
  access: '0' | '1' | '2' | '3';
  step_count?: number;
  scope_info?: ScopeInfo;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  created_by_user?: UserInfo;
  updated_by_user?: UserInfo;
}

/** One entry of a step's `reads` array — an earlier step's output, labelled (ORCH-286 §5.3). */
export interface WorkflowStepReadRef {
  step_key: string;
  label: string;
}

/** One entry of the engine's step-type registry, from core-api's `GET /metadata/workflow-step-types` (mirrors llm-service's `GET /llm-api/v1/capabilities`, ORCH-286 §5.4/P2). */
export interface StepTypeCapability {
  key: string;
  label: string;
  min_producers: number;
  max_producers: number | null;
  supports_review: boolean;
}

export interface CapabilitiesResponse {
  step_types: StepTypeCapability[];
}

export interface WorkflowStep {
  step_id: string;
  global_step_id?: string | null; // NULL for GLOBAL steps, references GLOBAL step UUID for overrides
  workflow_id: string;
  tenant_id: string;
  label: string;
  description?: string | null;
  sequence_order: number;
  next_steps: string[];
  status_flags: string[];
  ai_prompt?: string | null;
  has_sub_steps?: boolean;
  requires_approval?: boolean;
  /** Engine phase keys this business step covers (derived, read-only — ORCH-286 §5.3). */
  engine_phases?: string[];
  assigned_agents?: string[];
  on_set_effects: string[];
  on_leave_effects: string[];
  /** Stable machine key. Immutable after create (ORCH-286 §5.3). */
  step_key?: string | null;
  /** Engine capability this step runs (produce, parallel_produce, form_intake). NULL = business lifecycle step. */
  step_type?: string | null;
  /** AI agent ID (kind: reviewer) that reviews this step's output. */
  reviewer_agent?: string | null;
  /** Ordered context blocks read from earlier steps' outputs. */
  reads?: WorkflowStepReadRef[];
  review_subject_label?: string | null;
  max_review_loops?: number | null;
  summary_heading?: string | null;
  status: 'active' | 'disabled' | 'deleted';
  scope_info?: ScopeInfo;
  workflow_definition_label?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  created_by_user?: UserInfo;
  updated_by_user?: UserInfo;
}

// Form data types
export interface WorkflowDefinitionFormData {
  workflow_id: string;
  tenant_id: string;
  label: string;
  description?: string | null;
  models: string[];
  access?: string;
}

export interface WorkflowDefinitionUpdateData {
  label?: string;
  description?: string | null;
  models?: string[];
  access?: string;
  status?: string;
}

export interface WorkflowStepFormData {
  workflow_id: string;
  tenant_id: string;
  label: string;
  description?: string | null;
  status_flags?: string[];
  assigned_agents?: string[];
  next_steps?: string[];
  on_set_effects?: string[];
  on_leave_effects?: string[];
  step_key?: string | null;
  step_type?: string | null;
  reviewer_agent?: string | null;
  reads?: WorkflowStepReadRef[];
  review_subject_label?: string | null;
  max_review_loops?: number | null;
  summary_heading?: string | null;
  requires_approval?: boolean;
}

export interface WorkflowStepUpdateData {
  label?: string;
  description?: string | null;
  status_flags?: string[];
  assigned_agents?: string[];
  next_steps?: string[];
  on_set_effects?: string[];
  on_leave_effects?: string[];
  status?: string;
  step_key?: string | null;
  step_type?: string | null;
  reviewer_agent?: string | null;
  reads?: WorkflowStepReadRef[];
  review_subject_label?: string | null;
  max_review_loops?: number | null;
  summary_heading?: string | null;
  requires_approval?: boolean;
}

export interface StepOrderItem {
  step_id: string;
  sequence_order: number;
}

export interface WorkflowStepReorderData {
  workflow_id: string;
  tenant_id: string;
  step_order: StepOrderItem[];
}

// Query parameter types
export interface WorkflowDefinitionQueryParams {
  tenant_id?: string;
  status?: string;
  model_id?: string;
}

export interface WorkflowStepQueryParams {
  tenant_id?: string;
  workflow_id?: string;
  status?: string;
}

// Fixed enum values for status flags and effects
export const WORKFLOW_STATUS_FLAGS = [
  'Approved',
  'Approval',
  'Draft',
  'Published',
  'In Progress',
  'Completed',
] as const;

export const WORKFLOW_STEP_EFFECTS = ['Send email', 'Update data', 'Create Version'] as const;

// =============================================================================
// WORKFLOW SERVICE CLASS
// =============================================================================

class WorkflowServiceImpl {
  private baseUrl = '/metadata';

  // =============================================================================
  // WORKFLOW DEFINITIONS
  // =============================================================================

  async getWorkflowDefinitions(
    params: WorkflowDefinitionQueryParams = {}
  ): Promise<WorkflowDefinition[]> {
    const response = await apiService.api.get<WorkflowDefinition[]>(
      `${this.baseUrl}/workflow-definitions`,
      { params }
    );
    return response.data;
  }

  async getWorkflowDefinition(workflowId: string, tenantId: string): Promise<WorkflowDefinition> {
    const response = await apiService.api.get<WorkflowDefinition>(
      `${this.baseUrl}/workflow-definitions/${workflowId}/${tenantId}`
    );
    return response.data;
  }

  async createWorkflowDefinition(data: WorkflowDefinitionFormData): Promise<WorkflowDefinition> {
    const response = await apiService.api.post<WorkflowDefinition>(
      `${this.baseUrl}/workflow-definitions`,
      data
    );
    return response.data;
  }

  async updateWorkflowDefinition(
    workflowId: string,
    tenantId: string,
    data: WorkflowDefinitionUpdateData,
    currentTenantId?: string
  ): Promise<WorkflowDefinition> {
    const params = currentTenantId ? { current_tenant_id: currentTenantId } : {};
    const response = await apiService.api.put<WorkflowDefinition>(
      `${this.baseUrl}/workflow-definitions/${workflowId}/${tenantId}`,
      data,
      { params }
    );
    return response.data;
  }

  async deleteWorkflowDefinition(workflowId: string, tenantId: string): Promise<any> {
    const response = await apiService.api.delete(
      `${this.baseUrl}/workflow-definitions/${workflowId}/${tenantId}`
    );
    return response.data;
  }

  async revertWorkflowDefinition(workflowId: string, tenantId: string): Promise<any> {
    const response = await apiService.api.delete(
      `${this.baseUrl}/workflow-definitions/${workflowId}/${tenantId}/revert`
    );
    return response.data;
  }

  async overrideWorkflowDefinition(
    workflowId: string,
    tenantId: string
  ): Promise<WorkflowDefinition> {
    const response = await apiService.api.post<WorkflowDefinition>(
      `${this.baseUrl}/workflow-definitions/${workflowId}/override`,
      {},
      { params: { tenant_id: tenantId } }
    );
    return response.data;
  }

  // =============================================================================
  // WORKFLOW STEPS
  // =============================================================================

  async getWorkflowSteps(params: WorkflowStepQueryParams = {}): Promise<WorkflowStep[]> {
    const response = await apiService.api.get<WorkflowStep[]>(`${this.baseUrl}/workflow-steps`, {
      params,
    });
    return response.data;
  }

  async getWorkflowStep(stepId: string): Promise<WorkflowStep> {
    const response = await apiService.api.get<WorkflowStep>(
      `${this.baseUrl}/workflow-steps/${stepId}`
    );
    return response.data;
  }

  async createWorkflowStep(data: WorkflowStepFormData): Promise<WorkflowStep> {
    const response = await apiService.api.post<WorkflowStep>(
      `${this.baseUrl}/workflow-steps`,
      data
    );
    return response.data;
  }

  async updateWorkflowStep(
    stepId: string,
    data: WorkflowStepUpdateData,
    currentTenantId?: string
  ): Promise<WorkflowStep> {
    const params = currentTenantId ? { current_tenant_id: currentTenantId } : {};
    const response = await apiService.api.put<WorkflowStep>(
      `${this.baseUrl}/workflow-steps/${stepId}`,
      data,
      { params }
    );
    return response.data;
  }

  async deleteWorkflowStep(stepId: string): Promise<any> {
    const response = await apiService.api.delete(`${this.baseUrl}/workflow-steps/${stepId}`);
    return response.data;
  }

  async reorderWorkflowSteps(data: WorkflowStepReorderData): Promise<WorkflowStep[]> {
    const response = await apiService.api.put<WorkflowStep[]>(
      `${this.baseUrl}/workflow-steps/reorder`,
      data
    );
    return response.data;
  }

  // =============================================================================
  // ENGINE CAPABILITIES (ORCH-286 §5.4)
  // =============================================================================

  /**
   * Engine step-type registry, mirrored by core-api from llm-service's own
   * registry — never a frontend constant, and never a direct browser call to
   * llm-service (prod's ingress only routes `/` and `/api`, and llm-service's
   * other endpoints require an X-Internal-API-Key the browser doesn't have).
   */
  async getCapabilities(): Promise<CapabilitiesResponse> {
    const response = await apiService.api.get<CapabilitiesResponse>(
      `${this.baseUrl}/workflow-step-types`
    );
    return response.data;
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  getAccessLevelLabel(level: string | number): string {
    const labels: Record<string, string> = {
      '0': 'Internal Only',
      '1': 'Client Read-Only',
      '2': 'Client Overrideable',
      '3': 'Client Overrideable All',
    };
    return labels[String(level)] || 'Unknown';
  }

  getAccessLevelColor(level: string | number): 'error' | 'warning' | 'success' | 'default' {
    const colors: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
      '0': 'error',
      '1': 'warning',
      '2': 'success',
      '3': 'success',
    };
    return colors[String(level)] || 'default';
  }
}

const workflowService = new WorkflowServiceImpl();
export default workflowService;
